import type { ComponentType } from 'react'
import React from 'react'
import { withDependencies } from '@wix/thunderbolt-ioc'
import type { CompProps, Experiments } from '@wix/thunderbolt-symbols'
import { ExperimentsSymbol } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const isCsr = !!process.env.browser

let useControllerHook: (
	displayedId: string,
	compType: string,
	_compProps: CompProps,
	compId: string
) => {
	[x: string]: any
}
;(async () => {
	isCsr && (await window.externalsRegistry.react.loaded)
	useControllerHook = require('./hooks').useControllerHook
})()

// Merges two className strings without array allocation.
function mergeClassNames(a: string = '', b: string = ''): string {
	if (a && b) {
		return `${a} ${b}`
	}
	return a || b
}

function addMissingClassNames(className: string, classNamesToAdd: Array<string>): string {
	const existingClassNames = new Set(className.split(/\s+/).filter(Boolean))
	let result = className

	classNamesToAdd.forEach((classNameToAdd) => {
		if (!existingClassNames.has(classNameToAdd)) {
			result = result ? `${result} ${classNameToAdd}` : classNameToAdd
			existingClassNames.add(classNameToAdd)
		}
	})

	return result
}

export const RunControllersWrapper = withDependencies([ExperimentsSymbol], (experiments: Experiments) => {
	const addIdAsClassName = isExperimentOpen(experiments, 'specs.thunderbolt.addIdAsClassName')
	const preserveWixSelectClass = isExperimentOpen(experiments, 'specs.thunderbolt.preserveWixSelectClass')

	return {
		wrapComponent: (Component: ComponentType<any>) => {
			const Wrapper = ({
				compProps: storeProps,
				...restProps
			}: {
				compProps: any
				compId: string
				compClassType: string
				id: string
			}) => {
				const { id: displayedId, compId, compClassType } = restProps
				const compProps = useControllerHook(displayedId, compClassType, storeProps, compId)

				const props = {
					...compProps,
					...restProps,
				} as { [x: string]: any }

				// Repeater items need both ids: css can target either the base comp id or the inflated displayed id.
				const classNameIds = addIdAsClassName
					? displayedId === compId
						? [displayedId]
						: [compId, displayedId]
					: [compId]

				// Experiment on: merge Becky classes (compProps) + ID/wix-select (restProps).
				// Experiment off: legacy — compProps.className only.
				const existingClassName = preserveWixSelectClass
					? mergeClassNames(compProps?.className, (restProps as any)?.className)
					: (compProps?.className ?? '')

				const className = addMissingClassNames(existingClassName, classNameIds)

				return <Component {...props} className={className} />
			}
			return Wrapper
		},
	}
})
